import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from './i18n/LanguageContext';

// Maps known admin-typed English category/item names to translation keys.
// Items NOT in this map render as-is (Darija content, custom names).
// Lookup is case-insensitive and whitespace-trimmed.
const CATEGORY_TRANSLATION_KEYS = {
  'personal pronouns': 'cat_personal_pronouns',
  'possessive pronouns': 'cat_possessive_pronouns',
  'interrogative pronouns': 'cat_interrogative_pronouns',
  'demonstrative pronouns': 'cat_demonstrative_pronouns',
  'letters': 'cat_letters',
  'numbers': 'cat_numbers',
  'letters & numbers': 'letters_numbers',
};

const LearningPage = ({ API, isDarkMode, showNotification, currentUser, userToken, hasPaidPlan, setCurrentView, setInitialProfileTab }) => {
  const { t } = useTranslation();
  const translateLabel = (text) => {
    if (!text) return text;
    const normalized = String(text).trim().toLowerCase();
    const key = CATEGORY_TRANSLATION_KEYS[normalized];
    return key ? t(key) : text;
  };
  // App-shell caches: read from localStorage synchronously so the page
  // renders tabs + content from frame 1 instead of showing a full-page spinner.
  const _readCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedSettings = _readCache('learnSettingsCache');
  const _cachedAlphabet = _readCache('learnAlphabetCache');
  const _cachedPatterns = _readCache('learnPatternsCache');
  const _cachedPhrases = _readCache('learnPhrasesCache');
  const _cachedCustomTabs = _readCache('learnCustomTabsCache');
  const _cachedCustomTabItems = _readCache('learnCustomTabItemsCache');
  const _hasAnyLearnCache = !!(_cachedSettings || _cachedAlphabet || _cachedPatterns || _cachedPhrases);

  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState(_cachedSettings);
  const [alphabet, setAlphabet] = useState(_cachedAlphabet || []);
  const [patterns, setPatterns] = useState(_cachedPatterns || []);
  const [phrases, setPhrases] = useState(_cachedPhrases || []);
  const [loading, setLoading] = useState(!_hasAnyLearnCache);
  const [expandedCard, setExpandedCard] = useState(null);
  const [revealedPhrases, setRevealedPhrases] = useState({});
  const [showProModal, setShowProModal] = useState(false);
  const [customTabs, setCustomTabs] = useState(_cachedCustomTabs || []);
  const [customTabItems, setCustomTabItems] = useState(_cachedCustomTabItems || {});
  const [selectedItem, setSelectedItem] = useState(null);
  const baseTabs = [
    { label: t('letters_numbers'), icon: 'أ', icon_url: settings?.alphabet_icon_url || '', key: 'alphabet' },
    { label: t('sentence_builder'), icon: '🔧', icon_url: settings?.sentence_patterns_icon_url || '', key: 'sentence_patterns' },
    { label: t('common_patterns'), icon: '💬', icon_url: settings?.common_phrases_icon_url || '', key: 'common_phrases' },
  ];

  const tabs = [
    ...baseTabs,
    ...customTabs.map(ct => ({ label: translateLabel(ct.name), icon: ct.icon || '📝', icon_url: ct.icon_url || '', key: `custom_${ct.id}`, customTabId: ct.id, is_pro_only: ct.is_pro_only })),
  ];

  // Sound Library URLs require auth — append JWT as ?token= so HTML5 <audio> can play them.
  // Other URLs (legacy Dropbox / external) pass through unchanged.
  const withAudioAuth = (url) => {
    if (!url) return url;
    if (userToken && url.includes('/api/sound-library/play/') && !url.includes('token=')) {
      return url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(userToken);
    }
    return url;
  };

  const fetchData = useCallback(async () => {
    // Only show the loading spinner if we have no cached content yet.
    // Otherwise refresh silently in the background — the cached lessons stay visible.
    const hasContent = (alphabet?.length || patterns?.length || phrases?.length) > 0;
    if (!hasContent) setLoading(true);
    try {
      const authHeaders = userToken ? { 'Authorization': `Bearer ${userToken}` } : {};
      const [settingsRes, alphaRes, patternsRes, phrasesRes, customTabsRes] = await Promise.all([
        fetch(`${API}/learn-darija/settings`),
        fetch(`${API}/learn-darija/alphabet`, { headers: authHeaders }),
        fetch(`${API}/learn-darija/sentence-patterns`, { headers: authHeaders }),
        fetch(`${API}/learn-darija/common-phrases`, { headers: authHeaders }),
        fetch(`${API}/learn-darija/custom-tabs`, { headers: authHeaders }),
      ]);
      if (settingsRes.ok) { const d = await settingsRes.json(); setSettings(d); try { localStorage.setItem('learnSettingsCache', JSON.stringify(d)); } catch {} }
      if (alphaRes.ok) { const d = await alphaRes.json(); const items = d.items || d; setAlphabet(items); try { localStorage.setItem('learnAlphabetCache', JSON.stringify(items)); } catch {} }
      if (patternsRes.ok) { const d = await patternsRes.json(); const items = d.items || d; setPatterns(items); try { localStorage.setItem('learnPatternsCache', JSON.stringify(items)); } catch {} }
      if (phrasesRes.ok) { const d = await phrasesRes.json(); const items = d.items || d; setPhrases(items); try { localStorage.setItem('learnPhrasesCache', JSON.stringify(items)); } catch {} }
      if (customTabsRes.ok) {
        const ctData = await customTabsRes.json();
        const ctTabs = ctData.tabs || [];
        setCustomTabs(ctTabs);
        try { localStorage.setItem('learnCustomTabsCache', JSON.stringify(ctTabs)); } catch {}
        // Fetch items for each custom tab
        const itemsMap = {};
        await Promise.all(ctTabs.map(async (ct) => {
          try {
            const res = await fetch(`${API}/learn-darija/custom-tabs/${ct.id}/items`, { headers: authHeaders });
            if (res.ok) { const d = await res.json(); itemsMap[ct.id] = d.items || []; }
          } catch (e) { /* ignore */ }
        }));
        setCustomTabItems(itemsMap);
        try { localStorage.setItem('learnCustomTabItemsCache', JSON.stringify(itemsMap)); } catch {}
      }
    } catch (e) {
      console.error('Failed to load learning data:', e);
    }
    setLoading(false);
  }, [API, userToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isTabProOnly = (tabKey) => {
    if (!settings) return false;
    const tabSettings = settings.tabs || {};
    // Settings override (works for both base and custom tabs)
    if (tabSettings[tabKey]?.is_pro_only === true) return true;
    // Fallback: check custom tab's own is_pro_only field
    if (tabKey.startsWith('custom_')) {
      const ct = customTabs.find(t => `custom_${t.id}` === tabKey);
      return ct?.is_pro_only === true;
    }
    return false;
  };

  const isTabEnabled = (tabKey) => {
    if (!settings) return true;
    const tabSettings = settings.tabs || {};
    // Check settings for both base and custom tabs
    if (tabSettings[tabKey]?.enabled === false) return false;
    return true;
  };

  const gradientMap = {
    'purple-pink': { icon: 'from-purple-400 to-pink-400', bg: 'from-purple-50 to-pink-50', bgDark: 'from-purple-900/20 to-pink-900/20' },
    'blue-cyan': { icon: 'from-blue-400 to-cyan-300', bg: 'from-blue-50 to-cyan-50', bgDark: 'from-blue-900/20 to-cyan-900/20' },
    'emerald-teal': { icon: 'from-emerald-400 to-teal-300', bg: 'from-emerald-50 to-teal-50', bgDark: 'from-emerald-900/20 to-teal-900/20' },
    'orange-amber': { icon: 'from-orange-400 to-amber-300', bg: 'from-orange-50 to-amber-50', bgDark: 'from-orange-900/20 to-amber-900/20' },
    'rose-red': { icon: 'from-rose-400 to-red-400', bg: 'from-rose-50 to-red-50', bgDark: 'from-rose-900/20 to-red-900/20' },
    'violet-indigo': { icon: 'from-violet-400 to-indigo-400', bg: 'from-violet-50 to-indigo-50', bgDark: 'from-violet-900/20 to-indigo-900/20' },
    'sky-blue': { icon: 'from-sky-300 to-blue-400', bg: 'from-sky-50 to-blue-50', bgDark: 'from-sky-900/20 to-blue-900/20' },
    'lime-green': { icon: 'from-lime-400 to-green-400', bg: 'from-lime-50 to-green-50', bgDark: 'from-lime-900/20 to-green-900/20' },
    'fuchsia-purple': { icon: 'from-fuchsia-400 to-purple-500', bg: 'from-fuchsia-50 to-purple-50', bgDark: 'from-fuchsia-900/20 to-purple-900/20' },
    'amber-yellow': { icon: 'from-amber-400 to-yellow-300', bg: 'from-amber-50 to-yellow-50', bgDark: 'from-amber-900/20 to-yellow-900/20' },
    'teal-cyan': { icon: 'from-teal-400 to-cyan-400', bg: 'from-teal-50 to-cyan-50', bgDark: 'from-teal-900/20 to-cyan-900/20' },
    'pink-rose': { icon: 'from-pink-300 to-rose-400', bg: 'from-pink-50 to-rose-50', bgDark: 'from-pink-900/20 to-rose-900/20' },
    'indigo-blue': { icon: 'from-indigo-400 to-blue-300', bg: 'from-indigo-50 to-blue-50', bgDark: 'from-indigo-900/20 to-blue-900/20' },
    'red-orange': { icon: 'from-red-400 to-orange-300', bg: 'from-red-50 to-orange-50', bgDark: 'from-red-900/20 to-orange-900/20' },
    'cyan-emerald': { icon: 'from-cyan-400 to-emerald-400', bg: 'from-cyan-50 to-emerald-50', bgDark: 'from-cyan-900/20 to-emerald-900/20' },
    'yellow-lime': { icon: 'from-yellow-300 to-lime-400', bg: 'from-yellow-50 to-lime-50', bgDark: 'from-yellow-900/20 to-lime-900/20' },
    'purple-blue': { icon: 'from-purple-500 to-blue-400', bg: 'from-purple-50 to-blue-50', bgDark: 'from-purple-900/20 to-blue-900/20' },
    'green-emerald': { icon: 'from-green-400 to-emerald-300', bg: 'from-green-50 to-emerald-50', bgDark: 'from-green-900/20 to-emerald-900/20' },
    'slate-gray': { icon: 'from-slate-400 to-gray-400', bg: 'from-slate-50 to-gray-50', bgDark: 'from-slate-900/20 to-gray-900/20' },
    'rose-pink': { icon: 'from-rose-300 to-pink-400', bg: 'from-rose-50 to-pink-50', bgDark: 'from-rose-900/20 to-pink-900/20' },
  };
  const gradientKeys = Object.keys(gradientMap);

  const getGradient = (item, idx) => {
    if (item.gradient && gradientMap[item.gradient]) return gradientMap[item.gradient];
    return gradientMap[gradientKeys[idx % gradientKeys.length]];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p style={{ color: isDarkMode ? '#aaa' : '#666' }}>{t('loading_lessons')}</p>
        </div>
      </div>
    );
  }

  const showProGlass = !!settings?.page_is_pro_only && !hasPaidPlan;

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 pb-24 relative">
      {/* Header */}
      <div className="text-center py-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: isDarkMode ? '#fff' : '#1a1a2e' }}>
          {t('learn_darija')}
        </h1>
        <p className="text-sm" style={{ color: isDarkMode ? '#aaa' : '#666' }}>
          {t('learn_darija_subtitle')}
        </p>
      </div>

      {/* Tab bar — equal-width tabs with stacked icon + label */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', padding: '6px', borderRadius: '12px', backgroundColor: isDarkMode ? '#1e1e2e' : '#f1f5f9' }}>
        {tabs.map((tab, i) => {
          const enabled = isTabEnabled(tab.key);
          if (!enabled && !hasPaidPlan) return null;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(i)}
              style={{
                flex: '1 1 0%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                padding: '8px 4px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'center',
                overflow: 'hidden',
                transition: 'all 0.2s',
                boxShadow: activeTab === i ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                backgroundColor: activeTab === i ? '#7c3aed' : 'transparent',
                color: activeTab === i ? '#fff' : (isDarkMode ? '#aaa' : '#666'),
              }}
            >
              <span style={{display:'inline-flex',width:18,height:18,alignItems:'center',justifyContent:'center',flexShrink:0}}>
                {tab.icon_url && <img src={`${API.replace('/api', '')}${tab.icon_url}`} alt="" width="18" height="18" loading="eager" decoding="async" style={{ width: 18, height: 18, objectFit: 'contain' }} />}
              </span>
              <span style={{ fontSize: '10px', fontWeight: 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {!showProGlass && isTabProOnly(tabs[activeTab]?.key) && !hasPaidPlan ? (
        /* Full-page pro gate — matches Decoder/Dictionary pattern */
        <div className="flex items-center justify-center py-20">
          <div className="text-center p-8 max-w-md">
            <span style={{display:'flex',width:64,height:64,alignItems:'center',justifyContent:'center',flexShrink:0,margin:'0 auto 16px'}}>
              {tabs[activeTab]?.icon_url && <img src={`${API.replace('/api', '')}${tabs[activeTab].icon_url}`} alt="" width="64" height="64" loading="eager" decoding="async" style={{width:64,height:64,objectFit:'contain'}} />}
            </span>
            <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {tabs[activeTab]?.label}
            </h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {tabs[activeTab]?.label} {t('pro_feature_unlock')}
            </p>
            <button
              onClick={() => setShowProModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
            >
              {t('upgrade_to_pro')}
            </button>
          </div>
        </div>
      ) : (
      <div>
        {/* Tab 1: Letters & Numbers */}
        {activeTab === 0 && (() => {
          const categories = [];
          const seen = new Set();
          alphabet.forEach(item => {
            const cat = item.category || '';
            if (!seen.has(cat)) { seen.add(cat); categories.push(cat); }
          });

          return (
            <div>
              {categories.map((cat, catIdx) => {
                const catItems = alphabet.filter(item => (item.category || '') === cat);
                return (
                  <div key={cat || '_default_'}>
                    {/* Category separator */}
                    {catIdx > 0 && (
                      <div className="my-6 flex items-center gap-3">
                        <div className="flex-1 h-px" style={{ background: isDarkMode
                          ? 'linear-gradient(90deg, transparent, #7c3aed44, transparent)'
                          : 'linear-gradient(90deg, transparent, #c4b5fd, transparent)' }} />
                        {cat && <span className="text-sm font-semibold whitespace-nowrap px-2" style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>{translateLabel(cat)}</span>}
                        <div className="flex-1 h-px" style={{ background: isDarkMode
                          ? 'linear-gradient(90deg, transparent, #7c3aed44, transparent)'
                          : 'linear-gradient(90deg, transparent, #c4b5fd, transparent)' }} />
                      </div>
                    )}
                    {/* First category header */}
                    {catIdx === 0 && cat && (
                      <div className="mb-4 text-center">
                        <span className="text-sm font-semibold" style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>{translateLabel(cat)}</span>
                      </div>
                    )}
                    {/* Grid of cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {catItems.map((item, idx) => {
                        const isExpanded = expandedCard === item.id;
                        const grad = getGradient(item, idx);
                        const symbolLen = (item.symbol || '').length;
                        const symbolSize = symbolLen > 3 ? 'text-xs' : symbolLen > 2 ? 'text-sm' : 'text-lg';
                        return (
                          <div
                            key={item.id || idx}
                            onClick={() => setExpandedCard(isExpanded ? null : item.id)}
                            className={`cursor-pointer rounded-xl border p-3 transition-all duration-300 ${
                              isExpanded ? 'col-span-2 sm:col-span-3 md:col-span-4' : ''
                            } bg-gradient-to-br ${isDarkMode ? grad.bgDark : grad.bg}`}
                            style={{
                              backgroundColor: isExpanded
                                ? (isDarkMode ? '#2a2a3e' : '#fff')
                                : undefined,
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad.icon} flex items-center justify-center text-white font-bold ${symbolSize}`}>
                                {item.symbol}
                              </div>
                              <span className="text-2xl" style={{ color: isDarkMode ? '#fff' : '#1a1a2e' }}>{item.arabic_letter || item.arabic || ''}</span>
                            </div>
                            <p className="font-semibold text-sm" style={{ color: isDarkMode ? '#fff' : '#1a1a2e' }}>
                              {translateLabel(item.name)}
                            </p>
                            <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: isDarkMode ? '#888' : '#999' }}>
                              {item.difficulty ? t(item.difficulty) : t('beginner')}
                            </span>

                            {isExpanded && (
                              <div className="mt-4 pt-3 border-t" style={{ borderColor: isDarkMode ? '#444' : '#e2e8f0' }}>
                                {(item.pronunciation || item.tips || item.pronunciation_tip) && (
                                  <div className="mb-3 p-2 rounded-lg" style={{ backgroundColor: isDarkMode ? '#1e1e2e' : '#f8fafc' }}>
                                    <p className="text-xs font-medium mb-1" style={{ color: isDarkMode ? '#aaa' : '#666' }}>{t('pronunciation')}</p>
                                    <p className="text-sm" style={{ color: isDarkMode ? '#ddd' : '#333' }}>{item.pronunciation || item.tips || item.pronunciation_tip}</p>
                                  </div>
                                )}
                                {item.examples && item.examples.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium mb-2" style={{ color: isDarkMode ? '#aaa' : '#666' }}>{t('examples')}</p>
                                    {item.examples.map((ex, ei) => (
                                      <div key={ei} className="flex items-center justify-between py-1.5 px-2 rounded mb-1" style={{ backgroundColor: isDarkMode ? '#1e1e2e' : '#f8fafc' }}>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-bold text-sm" style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>{ex.word || ex.darija}</span>
                                          {ex.arabic && (
                                            <span className="text-sm" style={{ color: isDarkMode ? '#ddd' : '#333', fontFamily: 'serif' }}>{ex.arabic}</span>
                                          )}
                                          <span className="text-xs" style={{ color: isDarkMode ? '#aaa' : '#666' }}>— {ex.english}</span>
                                        </div>
                                        {settings?.audio_enabled !== false && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); if (ex.audio_url) new Audio(withAudioAuth(ex.audio_url)).play(); }}
                                          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs ml-1"
                                          style={{ backgroundColor: isDarkMode ? '#3b275e' : '#f3e8ff', color: '#a78bfa' }}
                                        >
                                          🔊
                                        </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {item.audio_url && settings?.audio_enabled !== false && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); new Audio(withAudioAuth(item.audio_url)).play(); }}
                                    className="mt-3 w-full py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium hover:opacity-90 transition-opacity"
                                  >
                                    🔊 {t('play_sound')}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {alphabet.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-4xl mb-2">📚</p>
                  <p style={{ color: isDarkMode ? '#aaa' : '#666' }}>{t('no_alphabet_items')}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Tab 2: Sentence Builder */}
        {activeTab === 1 && (
          <div className="space-y-3">
            {patterns.map((pattern, idx) => {
              const isOpen = expandedCard === pattern.id;
              return (
                <div
                  key={pattern.id || idx}
                  className="rounded-xl border overflow-hidden transition-all"
                  style={{
                    borderColor: isDarkMode ? '#333' : '#e2e8f0',
                    backgroundColor: isDarkMode ? '#1e1e2e' : '#fff',
                  }}
                >
                  <button
                    onClick={() => setExpandedCard(isOpen ? null : pattern.id)}
                    className="w-full flex items-center justify-between p-4 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-lg">
                        {pattern.icon || '📝'}
                      </div>
                      <div>
                        <p className="font-semibold text-sm" style={{ color: isDarkMode ? '#fff' : '#1a1a2e' }}>
                          {pattern.title}
                        </p>
                        {pattern.subtitle && (
                          <p className="text-xs" style={{ color: isDarkMode ? '#888' : '#999' }}>{pattern.subtitle}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-lg transition-transform" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: isDarkMode ? '#aaa' : '#666' }}>
                      ▼
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4">
                      {/* Formula */}
                      {pattern.formula && (
                        <div className="mb-3 p-3 rounded-lg text-center" style={{ backgroundColor: isDarkMode ? '#2a2a3e' : '#f0f4ff' }}>
                          <p className="text-xs uppercase tracking-wider mb-1 font-medium" style={{ color: isDarkMode ? '#888' : '#999' }}>{t('formula')}</p>
                          <p className="text-lg font-bold" style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>
                            {pattern.formula}
                          </p>
                        </div>
                      )}

                      {/* Explanation */}
                      {pattern.explanation && (
                        <p className="text-sm mb-3" style={{ color: isDarkMode ? '#ccc' : '#444' }}>
                          {pattern.explanation}
                        </p>
                      )}

                      {/* Visual breakdown */}
                      {pattern.breakdown && (
                        <div className="mb-3 p-3 rounded-lg" style={{ backgroundColor: isDarkMode ? '#2a2a3e' : '#faf5ff' }}>
                          <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: isDarkMode ? '#888' : '#999' }}>{t('breakdown')}</p>
                          <p className="text-sm font-mono" style={{ color: isDarkMode ? '#e9d5ff' : '#6b21a8' }}>
                            {pattern.breakdown}
                          </p>
                        </div>
                      )}

                      {/* Examples */}
                      {pattern.examples && pattern.examples.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs uppercase tracking-wider mb-2 font-medium" style={{ color: isDarkMode ? '#888' : '#999' }}>{t('examples')}</p>
                          {pattern.examples.map((ex, ei) => (
                            <div key={ei} className="flex items-center gap-2 flex-wrap py-1.5 px-2 rounded mb-1" style={{ backgroundColor: isDarkMode ? '#2a2a3e' : '#f8fafc' }}>
                              <span className="font-semibold text-sm" style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>{ex.darija}</span>
                              {ex.arabic && (
                                <span className="text-sm" style={{ color: isDarkMode ? '#ddd' : '#333', fontFamily: 'serif', direction: 'rtl' }}>{ex.arabic}</span>
                              )}
                              <span className="text-xs" style={{ color: isDarkMode ? '#888' : '#999' }}>→</span>
                              <span className="text-sm" style={{ color: isDarkMode ? '#ccc' : '#444' }}>{ex.english}</span>
                            </div>
                          ))}
                        </div>
                      )}

                    </div>
                  )}
                </div>
              );
            })}
            {patterns.length === 0 && (
              <div className="text-center py-12">
                <p className="text-4xl mb-2">🔧</p>
                <p style={{ color: isDarkMode ? '#aaa' : '#666' }}>{t('no_sentence_patterns')}</p>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Common Patterns */}
        {activeTab === 2 && (
          <div className="space-y-3">
            {phrases.map((phrase, idx) => {
              const isRevealed = revealedPhrases[phrase.id];
              return (
                <div
                  key={phrase.id || idx}
                  onClick={() => setRevealedPhrases(prev => ({ ...prev, [phrase.id]: !prev[phrase.id] }))}
                  className="cursor-pointer rounded-xl border p-4 transition-all"
                  style={{
                    borderColor: isDarkMode ? '#333' : '#e2e8f0',
                    backgroundColor: isDarkMode ? '#1e1e2e' : '#fff',
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold" style={{ color: isDarkMode ? '#fff' : '#1a1a2e' }}>
                      "{phrase.english_template}"
                    </p>
                    <span className="text-xs px-2 py-1 rounded-full" style={{
                      backgroundColor: isDarkMode ? '#2a2a3e' : '#f1f5f9',
                      color: isDarkMode ? '#aaa' : '#666',
                    }}>
                      {isRevealed ? t('tap_to_hide') : t('tap_to_reveal')}
                    </span>
                  </div>

                  <div className={`transition-all duration-300 overflow-hidden ${isRevealed ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: isDarkMode ? '#2a2a3e' : '#faf5ff' }}>
                      <p className="text-lg font-bold mb-1" style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>
                        {phrase.darija_template}
                      </p>
                      {phrase.arabic_template && (
                        <p className="text-base mb-1" style={{ color: isDarkMode ? '#ddd' : '#333', fontFamily: 'serif', direction: 'rtl' }}>
                          {phrase.arabic_template}
                        </p>
                      )}
                      {phrase.pronunciation && (
                        <p className="text-xs mb-3" style={{ color: isDarkMode ? '#888' : '#999' }}>
                          {t('pronunciation')}: {phrase.pronunciation}
                        </p>
                      )}

                      {phrase.examples && phrase.examples.length > 0 && (
                        <div className="space-y-1.5 mt-2">
                          {phrase.examples.map((ex, ei) => (
                            <div key={ei} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ backgroundColor: isDarkMode ? '#1e1e2e' : '#f8fafc' }}>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium" style={{ color: isDarkMode ? '#e9d5ff' : '#6b21a8' }}>{ex.darija}</span>
                                  {ex.arabic && <span className="text-sm" style={{ color: isDarkMode ? '#ddd' : '#333', fontFamily: 'serif' }}>{ex.arabic}</span>}
                                </div>
                                <span className="text-xs" style={{ color: isDarkMode ? '#888' : '#999' }}>{ex.english}</span>
                              </div>
                              {ex.audio_url && settings?.audio_enabled !== false && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); new Audio(withAudioAuth(ex.audio_url)).play(); }}
                                  className="w-7 h-7 rounded-full flex items-center justify-center bg-purple-100 text-purple-600 text-xs"
                                >
                                  🔊
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {!isRevealed && (
                    <div className="mt-1 h-8 rounded-lg overflow-hidden" style={{ backgroundColor: isDarkMode ? '#2a2a3e' : '#f1f5f9' }}>
                      <div className="h-full w-full flex items-center justify-center" style={{
                        background: isDarkMode
                          ? 'linear-gradient(90deg, transparent, rgba(124,58,237,0.15), transparent)'
                          : 'linear-gradient(90deg, transparent, rgba(124,58,237,0.08), transparent)',
                      }}>
                        <span className="text-xs" style={{ color: isDarkMode ? '#666' : '#aaa' }}>
                          ● ● ● {t('tap_to_see_darija')} ● ● ●
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {phrases.length === 0 && (
              <div className="text-center py-12">
                <p className="text-4xl mb-2">💬</p>
                <p style={{ color: isDarkMode ? '#aaa' : '#666' }}>{t('no_common_phrases')}</p>
              </div>
            )}
          </div>
        )}

        {/* Custom tabs content */}
        {activeTab >= 3 && tabs[activeTab]?.customTabId && (() => {
          const tabId = tabs[activeTab].customTabId;
          const items = customTabItems[tabId] || [];
          const genderColors = { female: { bg: '#fce4ec', text: '#c2185b', bgDark: '#4a1942', textDark: '#f48fb1' }, male: { bg: '#e3f2fd', text: '#1565c0', bgDark: '#1a3a5c', textDark: '#90caf9' } };
          const numberColors = { singular: { bg: '#f3e5f5', text: '#7b1fa2', bgDark: '#3a1a4a', textDark: '#ce93d8' }, plural: { bg: '#e8f5e9', text: '#2e7d32', bgDark: '#1a3a1a', textDark: '#a5d6a7' } };
          return (
            <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {items.map((item, idx) => {
                const grad = getGradient(item, idx);
                return (
                  <div
                    key={item.id || idx}
                    onClick={() => setSelectedItem(item)}
                    className={`rounded-xl border p-3 transition-all bg-gradient-to-br cursor-pointer active:scale-95 ${isDarkMode ? grad.bgDark : grad.bg}`}
                    style={{ borderColor: isDarkMode ? '#333' : '#e2e8f0' }}
                  >
                    {/* English */}
                    <p className="font-semibold text-xs md:text-base" style={{ color: isDarkMode ? '#fff' : '#1a1a2e' }}>
                      {item.english}
                    </p>
                    {/* Darija Latin */}
                    <p className="text-xs md:text-base font-medium mt-1" style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed' }}>
                      {item.darija_latin}
                    </p>
                    {/* Arabic */}
                    {item.arabic && (
                      <p className="text-xs md:text-base mt-1" style={{ color: isDarkMode ? '#ddd' : '#333', fontFamily: 'serif' }}>
                        {item.arabic}
                      </p>
                    )}
                    {/* Note */}
                    {item.note && (
                      <p className="text-[10px] md:text-xs mt-1.5 italic" style={{ color: isDarkMode ? '#888' : '#999' }}>
                        {item.note}
                      </p>
                    )}
                    {/* Tags: gender + number */}
                    {(item.gender || item.number) && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {item.gender && genderColors[item.gender] && (
                          <span className="text-[9px] md:text-[11px] font-semibold px-1.5 py-0.5 rounded-full" style={{
                            backgroundColor: isDarkMode ? genderColors[item.gender].bgDark : genderColors[item.gender].bg,
                            color: isDarkMode ? genderColors[item.gender].textDark : genderColors[item.gender].text,
                          }}>
                            {item.gender === 'female' ? `♀ ${t('female')}` : `♂ ${t('male')}`}
                          </span>
                        )}
                        {item.number && numberColors[item.number] && (
                          <span className="text-[9px] md:text-[11px] font-semibold px-1.5 py-0.5 rounded-full" style={{
                            backgroundColor: isDarkMode ? numberColors[item.number].bgDark : numberColors[item.number].bg,
                            color: isDarkMode ? numberColors[item.number].textDark : numberColors[item.number].text,
                          }}>
                            {item.number === 'singular' ? 'Singular' : 'Plural'}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Audio play button */}
                    {item.audio_url && settings?.audio_enabled !== false && (
                      <button
                        onClick={(e) => { e.stopPropagation(); new Audio(withAudioAuth(item.audio_url)).play(); }}
                        className="mt-2 w-full py-1.5 md:py-2 rounded-lg text-white text-[10px] md:text-xs font-medium hover:opacity-90 transition-opacity"
                        style={{ background: 'linear-gradient(to right, #8b5cf6, #ec4899)' }}
                      >
                        🔊 {t('play_sound')}
                      </button>
                    )}
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="col-span-2 md:col-span-3 text-center py-12">
                  <p className="text-4xl mb-2">{tabs[activeTab]?.icon || '📝'}</p>
                  <p style={{ color: isDarkMode ? '#aaa' : '#666' }}>{t('no_items_yet')}</p>
                </div>
              )}
            </div>

            {/* Tap-to-expand overlay */}
            {selectedItem && (() => {
              const si = selectedItem;
              return (
                <div
                  className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-5"
                  onClick={() => setSelectedItem(null)}
                >
                  <div
                    className={`relative max-w-sm w-full rounded-3xl overflow-hidden shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Close button */}
                    <button
                      onClick={() => setSelectedItem(null)}
                      className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full z-10 text-sm transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    >
                      ✕
                    </button>

                    <div className="p-5 space-y-3">
                      {/* English block */}
                      <div className="rounded-2xl p-4 text-center" style={{ background: isDarkMode ? 'linear-gradient(135deg, #1e3a5f, #1a2744)' : 'linear-gradient(135deg, #dbeafe, #eff6ff)' }}>
                        <span className={`text-[10px] uppercase tracking-widest font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`}>{t('english_label')}</span>
                        <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {si.english}
                        </p>
                      </div>

                      {/* Darija Latin block */}
                      <div className="rounded-2xl p-4 text-center" style={{ background: isDarkMode ? 'linear-gradient(135deg, #2d1b4e, #1a1033)' : 'linear-gradient(135deg, #ede9fe, #f5f3ff)' }}>
                        <span className={`text-[10px] uppercase tracking-widest font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>{t('darija_label')}</span>
                        <p className="text-2xl font-bold mt-1" style={{ color: isDarkMode ? '#c4b5fd' : '#7c3aed' }}>
                          {si.darija_latin}
                        </p>
                      </div>

                      {/* Arabic block */}
                      {si.arabic && (
                        <div className="rounded-2xl p-4 text-center" style={{ background: isDarkMode ? 'linear-gradient(135deg, #1a3a2a, #0f2318)' : 'linear-gradient(135deg, #d1fae5, #ecfdf5)' }}>
                          <span className={`text-[10px] uppercase tracking-widest font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`}>{t('arabic_label')}</span>
                          <p className="text-2xl font-bold mt-1" style={{ color: isDarkMode ? '#6ee7b7' : '#059669', fontFamily: 'serif' }}>
                            {si.arabic}
                          </p>
                        </div>
                      )}

                      {/* Note */}
                      {si.note && (
                        <p className={`text-sm italic text-center px-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {si.note}
                        </p>
                      )}

                      {/* Tags */}
                      {(si.gender || si.number) && (
                        <div className="flex gap-2 justify-center">
                          {si.gender && genderColors[si.gender] && (
                            <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{
                              backgroundColor: isDarkMode ? genderColors[si.gender].bgDark : genderColors[si.gender].bg,
                              color: isDarkMode ? genderColors[si.gender].textDark : genderColors[si.gender].text,
                            }}>
                              {si.gender === 'female' ? `♀ ${t('female')}` : `♂ ${t('male')}`}
                            </span>
                          )}
                          {si.number && numberColors[si.number] && (
                            <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{
                              backgroundColor: isDarkMode ? numberColors[si.number].bgDark : numberColors[si.number].bg,
                              color: isDarkMode ? numberColors[si.number].textDark : numberColors[si.number].text,
                            }}>
                              {si.number === 'singular' ? 'Singular' : 'Plural'}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Audio */}
                      {si.audio_url && settings?.audio_enabled !== false && (
                        <button
                          onClick={() => new Audio(withAudioAuth(si.audio_url)).play()}
                          className="w-full py-3 rounded-full text-white text-sm font-bold hover:opacity-90 transition-opacity"
                          style={{ background: 'linear-gradient(to right, #8b5cf6, #ec4899)' }}
                        >
                          🔊 {t('play_sound')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            </>
          );
        })()}
      </div>
      )}

      {/* Pro Upgrade Modal */}
      {showProModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setShowProModal(false)}>
          <div className={`rounded-2xl p-8 max-w-md w-full text-center shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className="text-6xl mb-4">🔒</div>
            <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('premium_content')}
            </h3>
            <p className={`text-base mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('premium_content_message')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowProModal(false)}
                className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {t('maybe_later')}
              </button>
              <button
                onClick={() => { setShowProModal(false); if (setInitialProfileTab) setInitialProfileTab('membership'); if (setCurrentView) setCurrentView('profile'); }}
                className="flex-1 py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg"
              >
                {t('upgrade_to_pro')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProGlass && (
        <>
          <div className="absolute inset-0 z-20 cursor-not-allowed"
               style={{ backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
                        backgroundColor: isDarkMode ? 'rgba(17,24,39,0.12)' : 'rgba(255,255,255,0.12)' }} />
          <div className="fixed z-30 pointer-events-none px-4" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(90vw, 28rem)' }}>
            <div className={`pointer-events-auto text-center p-8 max-w-md rounded-3xl shadow-2xl ring-4 ring-white/30 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'}`}>
              <span className="text-6xl mb-4 block">📚</span>
              <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('learn_darija')}</h2>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('learn_darija_pro_only')}
              </p>
              <button
                onClick={() => { if (setInitialProfileTab) setInitialProfileTab('membership'); if (setCurrentView) setCurrentView('profile'); }}
                className="px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold shadow-2xl hover:scale-105 transition-all flex items-center gap-2 mx-auto cursor-pointer">
                <span className="text-xl">🔓</span><span>{t('upgrade_to_pro')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LearningPage;
